<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { type PropType, computed, ref, watch, nextTick } from 'vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { ChevronsUpDown } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

  const themeColor = computed(() => useThemeColor().value)

  export interface ComboOption {
    value: string
    title: string
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, null] as PropType<string | null>,
      required: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    error: {
      type: String,
      required: false
    },
    class: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array as PropType<ComboOption[]>,
      required: true
    },
    asterisk: {
      type: Boolean,
      required: false,
      default: false
    },
    aiSource: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'input', 'blur', 'focus', 'validation-state'])

  const {
    value,
    errorMessage: internalErrorMessage,
    handleBlur,
    handleChange
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const touched = ref(false)
  const isDropdownOpen = ref(false)
  const inputRef = ref<HTMLInputElement | null>(null)
  const highlightedIndex = ref(-1)

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    emit('input', target.value)
    emit('update:modelValue', target.value)
    handleChange(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    // Open dropdown when typing
    if (!isDropdownOpen.value) {
      isDropdownOpen.value = true
    }
    highlightedIndex.value = -1
  }

  const handleInputBlur = (event: FocusEvent) => {
    touched.value = true
    handleBlur(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    emit('blur', event)
  }

  const handleInputFocus = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }

  const selectOption = (option: ComboOption) => {
    value.value = option.value
    emit('update:modelValue', option.value)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    isDropdownOpen.value = false
    highlightedIndex.value = -1
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (!isDropdownOpen.value && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      isDropdownOpen.value = true
      event.preventDefault()
      return
    }

    if (isDropdownOpen.value) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          highlightedIndex.value = Math.min(highlightedIndex.value + 1, props.options.length - 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
          break
        case 'Enter':
          event.preventDefault()
          if (highlightedIndex.value >= 0 && highlightedIndex.value < props.options.length) {
            selectOption(props.options[highlightedIndex.value])
          }
          break
        case 'Escape':
          isDropdownOpen.value = false
          highlightedIndex.value = -1
          break
      }
    }
  }

  // Focus input when popover opens
  watch(isDropdownOpen, (open) => {
    if (open) {
      nextTick(() => {
        inputRef.value?.focus()
      })
    }
  })
</script>

<template>
  <label
    v-if="label"
    class="mb-0 flex cursor-auto items-center text-sm font-medium"
  >
    <span class="truncate">{{ label }}</span>
    <span
      v-if="asterisk"
      class="text-base font-normal text-red-500"
    >
      *
    </span>
    <span
      v-if="aiSource"
      class="inline-flex h-5 w-5 items-center rounded bg-purple-200 p-1 text-xs font-semibold text-purple-700"
    >
      KI
    </span>
  </label>
  <div class="relative">
    <Popover v-model:open="isDropdownOpen">
      <PopoverTrigger as-child>
        <div class="relative">
          <input
            :id="props.id"
            ref="inputRef"
            v-model="value"
            class="block h-10 w-full rounded-lg px-3 py-2 pr-10 text-sm text-gray-800 placeholder:text-gray-500"
            :name="props.name"
            type="text"
            :placeholder="placeholder"
            :disabled="disabled"
            :class="[
              errorMessage
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : `border-gray-200 focus:border-${themeColor}-600 focus:ring-${themeColor}-600`,
              disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-text bg-white',
              props.class
            ]"
            :data-cy="dataCy"
            autocomplete="off"
            @input="handleInputChange"
            @blur="handleInputBlur"
            @focus="handleInputFocus"
            @keydown="handleKeydown"
          />
          <div
            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5"
            :class="{ 'cursor-not-allowed': disabled }"
          >
            <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-50" />
          </div>
          <div
            v-if="errorMessage"
            class="pointer-events-none absolute inset-y-0 right-8 flex items-center"
            :class="{ 'opacity-50': disabled }"
          >
            <ExclamationCircleIcon class="size-5 flex-shrink-0 text-red-500" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        class="w-[--radix-popover-trigger-width] p-0"
        align="start"
        :side-offset="4"
      >
        <div class="max-h-48 overflow-auto">
          <div
            v-for="(option, index) in options"
            :key="option.value"
            class="cursor-pointer px-3 py-2 text-sm text-gray-800 hover:bg-gray-100"
            :class="{ 'bg-gray-100': highlightedIndex === index }"
            @mousedown.prevent="selectOption(option)"
          >
            {{ option.title }}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
  <p
    v-if="errorMessage"
    class="mt-1 text-xs text-red-600"
    data-cy="input-error-message"
    :class="{ 'opacity-50': disabled }"
  >
    {{ errorMessage }}
  </p>
</template>
